import React, { useEffect, useRef, useState } from "react";
import { FaChevronDown } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import {
  destinationEntries,
} from "../../content/indiaSiteContent";
import { submitEnquiry } from "../../services/enquiry";

type FormData = {
  name: string;
  email: string;
  destination: string;
  nationality: string;
  phone: string;
  note: string;
  consent: boolean;
};

type Accordion = {
  id: number;
  title: string;
  items: string[];
};

const accordionData: Accordion[] = [
  {
    id: 1,
    title: "Passport & Identification",
    items: [
      "Original Passport required (6 months validity with at least two blank pages).",
      "Valid UAE Residence Visa with 90 days validity.",
      "3 Recent photographs with white background (3.5 x 4.5 CM).",
    ],
  },
  {
    id: 2,
    title: "Employment & Financial Documents",
    items: [
      "Original NOC letter stating salary, designation, joining date, and travel purpose for employees — addressed to the concerned Consulate / Embassy.",
      "Valid Trade License copy and self-introduction letter stating monthly income and passport details for Partners, Investors & Owners.",
      "Latest 6 months online bank statement with bank stamp or original bank statement.",
    ],
  },
  {
    id: 3,
    title: "Supporting Documents",
    items: [
      "Invitation letter from a friend or relative can be an additional advantage.",
      "Invitation letter required for Business visa applications.",
      "Urgent service available with additional charges.",
    ],
  },
];

const normalizeUaePhone = (value: string) => {
  const digits = value.replace(/\D/g, "");

  if (!digits) {
    return "";
  }

  if (digits.startsWith("91")) {
    return `+${digits}`;
  }

  if (digits.startsWith("0")) {
    return `+91${digits.slice(1)}`;
  }

  return `+91${digits}`;
};

const ApplicationSection: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [activeAccordion, setActiveAccordion] = useState<number | null>(1);
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countryOpen, setCountryOpen] = useState(false);
  const countryFieldRef = useRef<HTMLDivElement | null>(null);

  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    destination: "",
    nationality: "",
    phone: "",
    note: "",
    consent: false,
  });
  const destinationParam = new URLSearchParams(location.search).get(
    "destination",
  );
  const requestedDestination = destinationEntries.find(
    (destination) =>
      destination.slug === destinationParam ||
      destination.name.toLowerCase() === destinationParam?.toLowerCase(),
  );
  const selectedDestination =
    formData.destination || requestedDestination?.name || "";
  const destinationOptions = destinationEntries
    .map((destination) => ({
      value: destination.name,
      label: destination.name,
    }))
    .sort((left, right) => left.label.localeCompare(right.label));

  useEffect(() => {
    if (location.hash !== "#visa-application-form") {
      return;
    }

    const target = document.getElementById("visa-application-form");
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location.hash]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        countryFieldRef.current &&
        !countryFieldRef.current.contains(event.target as Node)
      ) {
        setCountryOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setCountryOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value, type } = event.target;
    const checked =
      event.target instanceof HTMLInputElement ? event.target.checked : false;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);
    setFormSuccess(null);
    const destinationValue = selectedDestination.trim();
    const normalizedPhone = normalizeUaePhone(formData.phone);

    if (
      !formData.name.trim() ||
      !formData.email.trim() ||
      !destinationValue ||
      !formData.nationality.trim() ||
      !normalizedPhone
    ) {
      setFormError(t("Please fill required fields"));
      return;
    }

    if (!formData.consent) {
      setFormError(t("Please provide consent before submitting your request."));
      return;
    }

    setIsSubmitting(true);

    const pagePath = `${window.location.pathname}${window.location.search}${window.location.hash}`;

    const payload = {
      fullName: formData.name.trim(),
      email: formData.email.trim(),
      phone: normalizedPhone,
      destination: destinationValue,
      nationality: formData.nationality.trim(),
      message: formData.note,
      leadType: "VISA",
      source: "Website UAE - Visa Application",
      sourcePage: "Visa Services",
      leadCountry: "UAE",
      country: "UAE",
      pagePath,
      pageUrl: window.location.href,
      utmSource: "website_uae",
      utmMedium: "visa_form",
      utmCampaign: "visa_application",
    };

    const result = await submitEnquiry(payload);
    setIsSubmitting(false);

    if (result.success) {
      setFormSuccess(t("Your visa application has been submitted successfully!"));
      setFormData({
        name: "",
        email: "",
        destination: "",
        nationality: "",
        phone: "",
        note: "",
        consent: false,
      });
    } else {
      setFormError(result.message || t("Failed to submit. Please try again."));
    }
  };

  return (
    <section id="visa-application-form" className="bg-gray-50 py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-12 lg:flex-row">
          <div className="w-full lg:w-1/2">
            <h3 className="mb-6 font-heading text-2xl font-bold text-brand-dark">
              {t("General Required Documents")}
            </h3>

            <p className="mb-8 text-gray-600">
              {t("Requirements may vary slightly depending on the destination and traveller profile.")}
            </p>

            <div className="space-y-4">
              {accordionData.map((item) => (
                <div
                  key={item.id}
                  className="overflow-hidden rounded-2xl border border-gray-200 bg-white"
                >
                  <button
                    type="button"
                    onClick={() =>
                      setActiveAccordion(
                        activeAccordion === item.id ? null : item.id,
                      )
                    }
                    className="flex w-full items-center justify-between px-6 py-4"
                  >
                    <span className="font-semibold text-brand-dark">
                      {t(item.title)}
                    </span>

                    <FaChevronDown
                      className={`transition-transform ${
                        activeAccordion === item.id ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {activeAccordion === item.id && (
                    <div className="px-6 pb-4 text-sm text-gray-600">
                      <ul className="list-disc space-y-2 pl-5">
                        {item.items.map((text) => (
                          <li key={text}>{t(text)}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="w-full lg:w-1/2">
            <div className="insp-card rounded-2xl border border-gray-100 bg-white p-8 shadow-sm">
              <h3 className="mb-2 font-heading text-2xl font-bold text-brand-dark">
                {t("Start Your Application")}
              </h3>

              <p className="mb-6 text-sm text-gray-500">
                {t("Our visa team will review your request and get back with the next steps.")}
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label className="insp-label">
                      {t("NAME")} <span className="text-brand-pink">*</span>
                    </label>
                    <input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="insp-input"
                      placeholder={t("Your full name")}
                      required
                    />
                  </div>

                  <div>
                    <label className="insp-label">
                      {t("EMAIL")} <span className="text-brand-pink">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="insp-input"
                      placeholder={t("you@example.com")}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label className="insp-label">
                      {t("Destination Country")}{" "}
                      <span className="text-brand-pink">*</span>
                    </label>
                    <div ref={countryFieldRef} className="relative">
                      <button
                        type="button"
                        onClick={() => setCountryOpen((value) => !value)}
                        className="flex h-11 w-full items-center justify-between rounded-xl border border-gray-200 bg-white px-4 text-left text-[15px] text-brand-dark shadow-sm transition focus:border-brand-pink focus:outline-none focus:ring-2 focus:ring-brand-pink/20"
                        aria-haspopup="listbox"
                        aria-expanded={countryOpen}
                      >
                        <span
                          className={
                            selectedDestination
                              ? "truncate text-brand-dark"
                              : "text-gray-400"
                          }
                        >
                          {selectedDestination || t("Select country")}
                        </span>
                        <FaChevronDown
                          className={`ml-3 shrink-0 text-xs text-gray-400 transition-transform ${
                            countryOpen ? "rotate-180" : ""
                          }`}
                        />
                      </button>

                      {countryOpen && (
                        <div className="absolute z-30 mt-2 w-full overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
                          <div className="border-b border-gray-100 px-4 py-3">
                            <p className="text-xs font-medium uppercase tracking-[0.18em] text-gray-400">
                              {t("Select country")}
                            </p>
                          </div>

                          <ul
                            role="listbox"
                            className="max-h-64 overflow-y-auto py-2"
                          >
                            {destinationOptions.length > 0 ? (
                              destinationOptions.map((destination) => (
                                <li key={destination.value}>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setFormData((prev) => ({
                                        ...prev,
                                        destination: destination.value,
                                      }));
                                      setCountryOpen(false);
                                    }}
                                    className={`flex w-full items-center px-4 py-2.5 text-left text-sm transition hover:bg-brand-pink/5 ${
                                      selectedDestination === destination.value
                                        ? "bg-brand-pink/10 text-brand-pink"
                                        : "text-brand-dark"
                                    }`}
                                  >
                                    {destination.label}
                                  </button>
                                </li>
                              ))
                            ) : (
                              <li className="px-4 py-3 text-sm text-gray-500">
                                {t("No destinations found")}
                              </li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="insp-label">
                      {t("Nationality")} <span className="text-brand-pink">*</span>
                    </label>
                    <input
                      type="text"
                      name="nationality"
                      value={formData.nationality}
                      onChange={handleChange}
                      className="insp-input h-12 bg-white text-sm shadow-sm"
                      placeholder={t("e.g. Indian Resident, UAE, British")}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="insp-label">
                    {t("PHONE NUMBER")} <span className="text-brand-pink">*</span>
                  </label>
                  <div className="flex overflow-hidden rounded-xl border border-gray-200 bg-white transition focus-within:border-brand-pink focus-within:ring-2 focus-within:ring-brand-pink/20">
                    <span className="flex items-center border-r border-gray-200 bg-gray-50 px-4 text-sm font-semibold text-brand-dark">
                      +91
                    </span>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 text-sm text-brand-dark placeholder:text-gray-400 focus:outline-none"
                      placeholder="570 123 4567"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="insp-label">{t("NOTE")}</label>
                  <textarea
                    name="note"
                    value={formData.note}
                    onChange={handleChange}
                    rows={5}
                    className="insp-input resize-none"
                    placeholder={t("Add a short note about your travel plans or visa requirement.")}
                  />
                </div>

                <label className="flex items-start gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                  <input
                    type="checkbox"
                    name="consent"
                    checked={formData.consent}
                    onChange={handleChange}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-brand-pink focus:ring-brand-pink"
                  />
                  <span>
                    {t("I agree to be contacted regarding my visa inquiry and application support.")}
                  </span>
                </label>

                <div className="space-y-3 pt-2">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="insp-btn-primary w-full disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isSubmitting ? t("Submitting...") : t("Submit Now")}
                  </button>

                  {formError && (
                    <div
                      className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-center text-sm text-red-700"
                      role="alert"
                    >
                      {formError}
                    </div>
                  )}

                  {formSuccess && (
                    <div
                      className="rounded-lg border border-green-100 bg-green-50 px-4 py-3 text-center text-sm text-green-700"
                      role="status"
                    >
                      {formSuccess}
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ApplicationSection;
